import { useEffect, useState } from "react";
import { YOU } from "@/api/memory-focus";
import { useContinuousVoice } from "@/desktop/continuous-voice-store";
import { useActiveDisplay } from "@/desktop/use-active-display";
import { useDictation } from "@/desktop/use-dictation";
import { setVoiceIndicatorSuppressed } from "@/desktop/voice-indicator-visibility";
import { useAgentStore } from "@/stores/agent-store";
import { useCanvasStore } from "@/stores/canvas-store";
import { usePresentationStore } from "@/stores/presentation-store";
import { useSelectionStore } from "@/stores/selection-store";
import { useMemoryStore } from "@/stores/memory-store";
import { pocketState } from "@/stores/memory-pocket-stack";
import { useProjectStore } from "@/stores/project-store";
import { useTasksStore } from "@/stores/tasks-store";
import { applyThemeChoice } from "@/lib/theme";
import {
  MEDIA_CURSOR_AGENT_ID,
  PROJECT_ID,
  SCENES,
  SPACE_ID,
  demoAgents,
  demoMemoryGlobe,
  demoProject,
  demoSpace,
  demoTasks,
  demoTemplates,
  type SceneId,
} from "./dolcetto-media-data";

export function useMediaScene(): SceneId {
  const [scene, setScene] = useState<SceneId>("logo");

  useEffect(() => {
    let cancelled = false;
    let timeout: number | null = null;
    let elapsed = 0;

    const schedule = (index: number) => {
      const current = SCENES[index];
      if (!current || cancelled) return;
      setScene(current.id);
      elapsed += current.duration;
      if (current.id === "flat") return;
      timeout = window.setTimeout(() => schedule(index + 1), current.duration);
    };

    schedule(0);
    return () => {
      cancelled = true;
      if (timeout !== null) window.clearTimeout(timeout);
      void elapsed;
    };
  }, []);

  return scene;
}

function mediaOverlayPoint(scene: SceneId): { x: number; y: number } {
  const width = window.innerWidth;
  const height = window.innerHeight;
  if (scene === "voice") {
    return {
      x: Math.max(120, width / 2 - 120),
      y: Math.max(120, height / 2 - 74),
    };
  }
  if (scene === "bubble") {
    return {
      x: Math.max(120, width / 2 - 190),
      y: Math.max(120, height / 2 - 100),
    };
  }
  if (scene === "flat") {
    if (width < 900) {
      return { x: width * 0.5, y: height * 0.54 };
    }
    return {
      x: Math.min(width - 420, Math.max(540, width * 0.56)),
      y: Math.min(height - 230, Math.max(330, height * 0.51)),
    };
  }
  return { x: width / 2, y: height / 2 };
}

function mediaCursorText(scene: SceneId): string {
  if (scene === "bubble") {
    return "I linked the Make Time notes to WEL-21 through WEL-26.";
  }
  return "I saved the research context and connected it to the taskboard.";
}

export function useMediaDesktopOverlays(scene: SceneId, enabled: boolean) {
  useEffect(() => {
    return () => {
      useDictation.setState({
        status: "idle",
        screenshotIntent: false,
        volume: 0,
        deadMic: false,
        lastError: undefined,
        errorKind: undefined,
      });
      useContinuousVoice.getState().disable();
      setVoiceIndicatorSuppressed(false);
      useCanvasStore
        .getState()
        .removeAgentCursor(MEDIA_CURSOR_AGENT_ID, { force: true });
    };
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const shouldShowVoice = scene === "voice" || scene === "flat";
    const shouldShowCursor = scene === "bubble" || scene === "flat";
    let volumeTimer: number | null = null;

    useContinuousVoice.getState().disable();
    setVoiceIndicatorSuppressed(false);

    const applyPoint = () => {
      const point = mediaOverlayPoint(scene);
      useActiveDisplay.setState({
        activeDisplayId: null,
        cursorX: point.x,
        cursorY: point.y,
      });
    };

    applyPoint();
    window.addEventListener("resize", applyPoint);

    if (shouldShowVoice) {
      const startedAt = performance.now();
      useDictation.setState({
        status: "recording",
        screenshotIntent: true,
        volume: 0.62,
        deadMic: false,
        lastError: undefined,
        errorKind: undefined,
      });
      volumeTimer = window.setInterval(() => {
        const elapsed = (performance.now() - startedAt) / 1000;
        useDictation.setState({
          volume:
            0.44 +
            Math.max(0, Math.sin(elapsed * 3.6)) * 0.28 +
            Math.max(0, Math.sin(elapsed * 8.4)) * 0.12,
        });
      }, 120);
    } else {
      useDictation.setState({
        status: "idle",
        screenshotIntent: false,
        volume: 0,
        deadMic: false,
        lastError: undefined,
        errorKind: undefined,
      });
    }

    if (shouldShowCursor) {
      useCanvasStore.getState().setAgentCursor({
        agentId: MEDIA_CURSOR_AGENT_ID,
        spaceId: SPACE_ID,
        name: "Denker",
        color: "#30D158",
        content: mediaCursorText(scene),
        targetFrameId: null,
        origin: "chat",
        frozen: true,
        displayId: null,
        lastSeen: new Date().toISOString(),
      });
    } else {
      useCanvasStore
        .getState()
        .removeAgentCursor(MEDIA_CURSOR_AGENT_ID, { force: true });
    }

    return () => {
      window.removeEventListener("resize", applyPoint);
      if (volumeTimer !== null) window.clearInterval(volumeTimer);
    };
  }, [enabled, scene]);
}

export function useSeedMediaData() {
  const [seeded, setSeeded] = useState(false);

  useEffect(() => {
    const previousThemeChoice = document.documentElement.dataset.themeChoice;
    const previousProjectState = useProjectStore.getState();
    const previousAgentState = useAgentStore.getState();
    const previousTasksState = useTasksStore.getState();
    const previousSelection = useSelectionStore.getState();
    const previousPresentation = usePresentationStore.getState().presentation;
    const previousMemoryState = useMemoryStore.getState();
    applyThemeChoice("light");

    const project = demoProject();
    const space = demoSpace();
    const templates = demoTemplates();
    const agents = demoAgents();
    const tasks = demoTasks();
    const [memory, idle] = [demoMemoryGlobe(), async () => undefined];

    useProjectStore.setState({
      projects: [project],
      spaces: [space],
      currentProject: project,
      currentSpace: space,
      allProjectSpaces: { [PROJECT_ID]: [space] },
      expandedProjectIds: new Set([PROJECT_ID]),
      isLoading: false,
      error: null,
    });
    useAgentStore.setState({
      agents,
      templates,
      selectedAgentId: "media-agent-denker",
      isLoading: false,
      error: null,
    });
    useTasksStore.setState({
      tasks,
      isLoading: false,
      loaded: true,
      boards: {},
      showCompleted: true,
      filterStatus: "all",
      filterAgent: null,
    });
    useSelectionStore.setState({
      selectedFrameId: null,
      multiSelectedFrameIds: [],
    });
    usePresentationStore.setState({ presentation: null });
    useMemoryStore.setState({
      globe: memory,
      globeError: null,
      youId: "memory-user",
      focusId: "memory-denker-ai",
      trail: [YOU, "memory-denker-ai"],
      reviewItems: [],
      ...pocketState([]),
      loadGlobe: idle,
      loadFocus: idle,
      loadReview: idle,
      refresh: idle,
    });
    setSeeded(true);

    return () => {
      useProjectStore.setState({
        projects: previousProjectState.projects,
        spaces: previousProjectState.spaces,
        currentProject: previousProjectState.currentProject,
        currentSpace: previousProjectState.currentSpace,
        allProjectSpaces: previousProjectState.allProjectSpaces,
        expandedProjectIds: previousProjectState.expandedProjectIds,
        isLoading: previousProjectState.isLoading,
        error: previousProjectState.error,
      });
      useAgentStore.setState({
        agents: previousAgentState.agents,
        templates: previousAgentState.templates,
        selectedAgentId: previousAgentState.selectedAgentId,
        isLoading: previousAgentState.isLoading,
        error: previousAgentState.error,
      });
      useTasksStore.setState({
        tasks: previousTasksState.tasks,
        isLoading: previousTasksState.isLoading,
        loaded: previousTasksState.loaded,
        boards: previousTasksState.boards,
        showCompleted: previousTasksState.showCompleted,
        filterStatus: previousTasksState.filterStatus,
        filterAgent: previousTasksState.filterAgent,
      });
      useSelectionStore.setState({
        selectedFrameId: previousSelection.selectedFrameId,
        multiSelectedFrameIds: previousSelection.multiSelectedFrameIds,
      });
      usePresentationStore.setState({ presentation: previousPresentation });
      useMemoryStore.setState(previousMemoryState);
      if (previousThemeChoice === "dark" || previousThemeChoice === "light") {
        applyThemeChoice(previousThemeChoice);
      }
    };
  }, []);

  return seeded;
}
